import { handleCreateUnreviewedReport } from '@/entities/inbox';
import { fetchEcgDataMap } from '@/entities/study';
import {
  showMessageError,
  showMessageInfo,
  adjustStudyDate,
  convertToMoment,
  dateToReportDate,
} from '@/shared/lib';
import { useMergeState, useUpdateEffect } from '@/shared/lib/customHooks';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { t } from 'ttag';
import type {
  CreateInstantReportModalProps,
  CreateInstantReportModalState,
} from './createInstantReportModal.types';

/**
 * Drives the date-range selection and submission of the create instant report modal.
 * Loads the study's reviewed ECG data map on mount, orders the two clicked dates into a range,
 * converts it to server time to create the unreviewed report, and keeps any already-existing
 * report for the range, clearing it when the range changes.
 *
 * @param props - Modal props carrying the study id, friendly id, start, stop, timezone, offsets
 * and the after-create and view-existing handlers.
 * @returns The modal state, the date click, create and view-report handlers, and the start and end
 * day boundaries.
 */
export const useCreateInstantReportModal = ({
  handleAfterCreatingInstantReport,
  start,
  stop,
  timezone,
  studyFid,
  studyTimeOffset,
  adjustedStudyDayOffset,
  studyId,
  handleViewExistedInstantReport,
}: Pick<
  CreateInstantReportModalProps,
  | 'handleAfterCreatingInstantReport'
  | 'start'
  | 'stop'
  | 'timezone'
  | 'studyFid'
  | 'studyTimeOffset'
  | 'adjustedStudyDayOffset'
  | 'studyId'
  | 'handleViewExistedInstantReport'
>) => {
  const [state, setState] = useMergeState<CreateInstantReportModalState>({
    startDate: null,
    endDate: null,
    ecgDataMap: {},
    isCreating: false,
    existedReport: null,
  });

  /**
   * Loads the reviewed ECG data map marking which study days can be reported on. Fetches it for the study friendly id and the study start and stop bounds into state, logging a failure without surfacing a message to the user.
   *
   * @returns A promise resolving once the ECG data map has been stored in state.
   */
  const handleEcgDataMapLoad = async () => {
    try {
      const ecgDataMap = await fetchEcgDataMap({
        studyFid: studyFid as unknown as number,
        start,
        stop,
        isReviewed: true,
      });
      setState({ ecgDataMap });
    } catch (error) {
      console.error('Failed to fetch ECG data map: ', error);
    }
  };

  /**
   * Builds the instant-report day range from successive calendar clicks. The first click sets the start, the second sets the end and swaps the two when the clicked day is earlier, and any further click restarts the range with a cleared end.
   *
   * @param date - Day that was clicked in the calendar.
   * @returns Nothing.
   */
  const handleDateClick = (date: any) => {
    if (!state.startDate) {
      setState({ startDate: date });
      return;
    }

    if (!state.endDate) {
      if (date.valueOf() < state.startDate.valueOf()) {
        setState({
          startDate: date,
          endDate: state.startDate,
        });
        return;
      }

      setState({ endDate: date });
      return;
    }

    setState({
      startDate: date,
      endDate: null,
    });
  };

  /**
   * Requests an instant report for the selected day range. Converts both days from study-adjusted dates back to server dates, turns them into report start and end timestamps in the study timezone shifted by the study time offset, then on success shows the processing notice and refreshes the report list, or stores the conflicting existing report, logging and surfacing any failure while clearing the creating flag.
   *
   * @returns A promise resolving once state reflects the created or the conflicting report.
   */
  const onClickCreate = async () => {
    try {
      setState({ isCreating: true });

      // the Create Instant Report button is disabled unless both dates are set
      const sDate = adjustStudyDate({
        dateMoment: state.startDate!.clone(),
        adjustedStudyDayOffset: adjustedStudyDayOffset as any,
        shouldRevertToServerDate: true,
      }).format('YYYY-MM-DD');
      const eDate = adjustStudyDate({
        dateMoment: state.endDate!.clone(),
        adjustedStudyDayOffset: adjustedStudyDayOffset as any,
        shouldRevertToServerDate: true,
      }).format('YYYY-MM-DD');
      let start = dateToReportDate({
        date: sDate,
        timezone,
        type: 'start',
        format: 'YYYY/MM/DD',
      });
      let stop = dateToReportDate({
        date: eDate,
        timezone,
        type: 'end',
        format: 'YYYY/MM/DD',
      });
      start += studyTimeOffset * 60 * 60 * 1000 * -1;
      stop += studyTimeOffset * 60 * 60 * 1000 * -1;

      const result = await handleCreateUnreviewedReport({
        studyId,
        start: dayjs(start).toISOString(),
        stop: dayjs(stop).toISOString(),
      });
      const { isSuccess = false, existedReport = null } = result ?? {};
      if (isSuccess) {
        showMessageInfo({
          content: t`Your request is being processed. The report will be ready within an hour.`,
        });
        setState({ isCreating: false });
        handleAfterCreatingInstantReport();
      } else {
        setState({
          isCreating: false,
          existedReport,
        });
      }
    } catch (error) {
      console.error('Failed to create instant report: ', error);
      showMessageError({ content: error instanceof Error ? error.message : undefined });
      setState({ isCreating: false });
    }
  };

  /**
   * Opens the instant report that already covers the requested range. Passes the id of the conflicting report held in state to the existing-report viewer.
   *
   * @returns Nothing.
   */
  const handleViewReportClick = () => {
    // only rendered when state.existedReport is set, see createInstantReportModal.tsx
    handleViewExistedInstantReport(state.existedReport!.id);
  };

  /**
   * Resolves the earliest selectable day for the instant-report date picker. Falls back to the start of today when the study has no start date, otherwise converts the start date into the study timezone and takes the start of that day.
   *
   * @returns Start-of-day moment bounding the selectable range.
   */
  const getStartDateLocalTime = () => {
    if (!start) {
      return dayjs().startOf('day');
    }

    const timeStart = (
      convertToMoment({
        isoDate: start,
        timezone,
        studyTimeOffset,
      }) ?? dayjs()
    ).startOf('day');
    return timeStart.startOf('day');
  };

  /**
   * Resolves the latest selectable day for the instant-report date picker. Falls back to the end of today when the study has no stop date, otherwise converts the stop date into the study timezone and takes the end of that day.
   *
   * @returns End-of-day moment bounding the selectable range.
   */
  const getEndDateLocalTime = () => {
    if (!stop) {
      return dayjs().endOf('day');
    }

    const timeEnd = (
      convertToMoment({
        isoDate: stop,
        timezone,
        studyTimeOffset,
      }) ?? dayjs()
    ).endOf('day');
    return timeEnd.endOf('day');
  };

  useEffect(() => {
    handleEcgDataMapLoad();
  }, []);

  useUpdateEffect(() => {
    setState({ existedReport: null });
  }, [state.startDate, state.endDate]);

  return {
    state,
    handleDateClick,
    onClickCreate,
    handleViewReportClick,
    getStartDateLocalTime,
    getEndDateLocalTime,
  };
};
